/**
 * SeoLandings — 8 SEO-optimised public pages (PT-BR) built as static React
 * routes that crawlers/AI assistants can fully index.
 *
 * Strategy: each page (a) sets `document.title` + meta tags dynamically via
 * `useSeo()` and (b) renders rich semantic HTML wrapped in `<article>` with
 * proper `<h1>/<h2>` hierarchy + schema.org JSON-LD. The shared CSS comes
 * from `/public/index.html .seo-fallback` so the visual is consistent with
 * the noscript fallback the crawler-without-JS sees on `/`.
 *
 * Routes registered:
 *   • /busca-de-leads-b2b
 *   • /gerenciamento-de-leads
 *   • /lead-scoring
 *   • /identificacao-de-empresas-por-ip
 *   • /crm-para-prospeccao
 *   • /politica-de-privacidade
 *   • /lgpd
 *   • /seguranca
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------ */
/*  SEO helpers                                                       */
/* ------------------------------------------------------------------ */
function useSeo({ title, description, canonical, jsonLd }) {
  useEffect(() => {
    document.title = title;
    const setMeta = (sel, content) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        if (sel.startsWith("meta[name=")) el.setAttribute("name", sel.match(/name="([^"]+)"/)[1]);
        else if (sel.startsWith("meta[property=")) el.setAttribute("property", sel.match(/property="([^"]+)"/)[1]);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
      link.setAttribute("href", canonical);
    }
    // JSON-LD — id-tag so we can replace on remount.
    const SCHEMA_ID = "seo-page-jsonld";
    document.getElementById(SCHEMA_ID)?.remove();
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = SCHEMA_ID;
      s.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
    // Restore the homepage title when we unmount so navigation back to /
    // doesn't show a stale title.
    return () => {
      document.title = "AgenteLead Pro — AI B2B Prospecting Cockpit · Find, Engage and Close Local Leads";
    };
  }, [title, description, canonical, jsonLd]);
}

/* ------------------------------------------------------------------ */
/*  Shared layout shell                                               */
/* ------------------------------------------------------------------ */
function Shell({ title, lede, children, breadcrumb }) {
  return (
    <main className="seo-fallback" role="main" data-testid="seo-landing"
          style={{
            fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
            color: "#0F172A", background: "#FBF9F4",
            maxWidth: 1100, margin: "0 auto",
            padding: "48px 24px", lineHeight: 1.6,
          }}>
      <nav className="text-[12px] text-slate-500 mb-2">
        <Link to="/" className="hover:underline">AgenteLead Pro</Link>
        <span className="mx-1.5">›</span>
        <span>{breadcrumb}</span>
      </nav>
      <header>
        <h1 style={{ fontSize: 36, fontWeight: 800, margin: "0 0 12px", letterSpacing: "-0.02em" }}>
          {title}
        </h1>
        <p style={{ fontSize: 18, color: "#0F172A", margin: "0 0 24px", maxWidth: 780 }}>
          {lede}
        </p>
        <Link to="/login"
              className="inline-block bg-[#84CC16] text-[#0F172A] px-6 py-3 rounded-lg font-bold no-underline mb-6"
              data-testid="seo-cta">
          Iniciar teste gratuito de 14 dias
        </Link>
      </header>
      {children}
      <footer className="text-[12px] text-slate-500 mt-12 border-t border-slate-200 pt-4">
        © 2026 AgenteLead Pro · <Link to="/politica-de-privacidade" className="hover:underline">Política de Privacidade</Link>
        {" · "}<Link to="/lgpd" className="hover:underline">LGPD</Link>
        {" · "}<Link to="/seguranca" className="hover:underline">Segurança</Link>
      </footer>
    </main>
  );
}

/* ================================================================== */
/*  1. /busca-de-leads-b2b                                            */
/* ================================================================== */
export function BuscaDeLeadsB2B() {
  useSeo({
    title: "Busca de Leads B2B com IA — AgenteLead Pro",
    description: "Encontre empresas reais por nicho, cidade e raio no Google Maps. Enriqueça com IP intelligence, score 0-100 e auditoria GPT-4o em segundos.",
    canonical: "https://agentelead-pro.com/busca-de-leads-b2b",
    jsonLd: {
      "@context": "https://schema.org", "@type": "Service",
      "name": "Busca de Leads B2B com IA",
      "provider": { "@type": "Organization", "name": "AgenteLead Pro" },
      "areaServed": ["BR", "US", "Global"],
      "description": "Plataforma de busca de leads B2B que combina Google Maps Places API, inteligência de IP, auditoria GPT-4o e score 0-100.",
    },
  });
  return (
    <Shell breadcrumb="Busca de Leads B2B"
           title="Busca de Leads B2B com Inteligência Artificial"
           lede="Encontre empresas reais por nicho, cidade e raio usando Google Maps Places API. Cada lead vem enriquecido com auditoria GPT-4o em menos de 90 segundos, score de oportunidade 0-100, contexto de IP (ASN, tipo de conexão, geolocalização aproximada) e dados de presença digital — pronto para abordagem comercial.">
      <section>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>O que torna a busca diferente</h2>
        <ul style={{ paddingLeft: 20, margin: "8px 0" }}>
          <li><strong>Fonte oficial:</strong> Google Maps Places API — sem scrapers frágeis.</li>
          <li><strong>Enriquecimento na busca:</strong> telefone, site, reviews, score digital e categorias.</li>
          <li><strong>Auditoria GPT-4o instantânea:</strong> clique e receba um briefing de presença digital.</li>
          <li><strong>Sinais de intenção:</strong> visitas ao seu site, tipo de conexão (corporativa / residencial / móvel), VPN/proxy.</li>
          <li><strong>Filtros de qualificação:</strong> nicho, raio, score mínimo, presença em redes sociais.</li>
        </ul>
      </section>
      <section>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Casos de uso</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 18 }}>
          {[
            ["Agências de marketing", "Prospecte SMBs por vertical e cidade. Faça o audit digital antes da call."],
            ["SDRs B2B", "Trabalhe 50 leads/dia com contexto e fala personalizada por GPT-4o."],
            ["Consultores", "Identifique empresas com baixa presença digital — sua oportunidade."],
            ["Empreiteiras", "Construa pipeline de contratantes e fornecedores recorrentes na sua região."],
          ].map(([title, desc]) => (
            <div key={title} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, background: "#fff" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>{title}</h3>
              <p style={{ margin: 0, color: "#334155", fontSize: 14 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>
    </Shell>
  );
}

/* ================================================================== */
/*  2. /gerenciamento-de-leads                                        */
/* ================================================================== */
export function GerenciamentoDeLeads() {
  useSeo({
    title: "Gerenciamento de Leads B2B em Funil Kanban — AgenteLead Pro",
    description: "CRM Kanban com drag-and-drop, owner lock, histórico de etapas e análise de funil. Visão NEW → CONTACTED → QUALIFIED → PROPOSAL → WON em uma tela.",
    canonical: "https://agentelead-pro.com/gerenciamento-de-leads",
    jsonLd: {
      "@context": "https://schema.org", "@type": "Service",
      "name": "Gerenciamento de Leads B2B",
      "provider": { "@type": "Organization", "name": "AgenteLead Pro" },
      "description": "CRM Kanban multi-tenant com funil de conversão analytics e histórico completo de cada lead.",
    },
  });
  return (
    <Shell breadcrumb="Gerenciamento de Leads"
           title="Gerenciamento de Leads em Funil Kanban"
           lede="Drag-and-drop por etapas, owner lock para evitar pisar no time, histórico completo por lead, drawer com timeline de atividades e métricas de conversão acumuladas. O CRM que você precisa para fechar com previsibilidade — sem planilhas paralelas.">
      <section>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Recursos do CRM</h2>
        <ul>
          <li><strong>Kanban</strong> com etapas customizáveis (NEW, CONTACTED, QUALIFIED, PROPOSAL, WON, LOST).</li>
          <li><strong>Owner lock</strong> — apenas o dono do lead pode mover; gestor tem override.</li>
          <li><strong>Drawer por lead</strong> com timeline de toda atividade (emails, replies, WhatsApp, edições).</li>
          <li><strong>Funil analítico</strong> com taxas stage-to-stage e revenue previsto.</li>
          <li><strong>Tags, notas, anexos</strong> e campos customizados por tenant.</li>
        </ul>
      </section>
    </Shell>
  );
}

/* ================================================================== */
/*  3. /lead-scoring                                                  */
/* ================================================================== */
export function LeadScoring() {
  useSeo({
    title: "Lead Scoring — Fórmula 40/25/20/10/5 — AgenteLead Pro",
    description: "Score 0-100 que combina perfil (40%), intenção (25%), contexto de IP (20%), origem da campanha (10%) e qualidade dos dados (5%). Buckets: blazing, hot, warm, cold.",
    canonical: "https://agentelead-pro.com/lead-scoring",
    jsonLd: {
      "@context": "https://schema.org", "@type": "Service",
      "name": "Lead Scoring com IA",
      "provider": { "@type": "Organization", "name": "AgenteLead Pro" },
      "description": "Sistema de pontuação 0-100 multi-fator para priorização comercial.",
    },
  });
  return (
    <Shell breadcrumb="Lead Scoring"
           title="Lead Scoring multi-fator — priorize o que importa"
           lede="Cada lead recebe um score 0-100 calculado por uma fórmula transparente combinando perfil do lead, intenção de navegação, contexto do IP, origem da campanha e qualidade dos dados. O vendedor vê instantaneamente quem vale uma call hoje, quem entra na cadência semanal e quem entra no nurturing.">
      <section>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Como a fórmula funciona</h2>
        <ul>
          <li><strong>40% — Perfil:</strong> setor-alvo, decisor identificado, telefone/email/site presentes.</li>
          <li><strong>25% — Intenção:</strong> visitas à página de preço, retornos, tempo no site, preenchimento de formulário.</li>
          <li><strong>20% — Contexto IP:</strong> IP corporativo vale mais que residencial; região-alvo vale extra; VPN/datacenter penaliza.</li>
          <li><strong>10% — Origem:</strong> CPC, direto, orgânico, referral, email.</li>
          <li><strong>5% — Qualidade:</strong> completude dos campos.</li>
        </ul>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Buckets visíveis</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 }}>
          {[
            ["🔥 Blazing", "80-100", "Liga hoje"],
            ["🔥 Hot", "65-79", "Cadência prioritária"],
            ["☀️ Warm", "45-64", "Nurturing semanal"],
            ["❄️ Cold", "0-44", "Reciclar / arquivar"],
          ].map(([label, range, action]) => (
            <div key={label} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 14, background: "#fff", textAlign: "center" }}>
              <div style={{ fontWeight: 700 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0F172A" }}>{range}</div>
              <div style={{ fontSize: 12, color: "#64748B" }}>{action}</div>
            </div>
          ))}
        </div>
      </section>
    </Shell>
  );
}

/* ================================================================== */
/*  4. /identificacao-de-empresas-por-ip                              */
/* ================================================================== */
export function IdentificacaoPorIP() {
  useSeo({
    title: "Identificação de Empresas por IP — AgenteLead Pro",
    description: "Inteligência de IP para B2B: ASN, organização provável, tipo de conexão, geolocalização aproximada e detecção de VPN/proxy/datacenter. Anonimização LGPD-first.",
    canonical: "https://agentelead-pro.com/identificacao-de-empresas-por-ip",
    jsonLd: {
      "@context": "https://schema.org", "@type": "Service",
      "name": "Inteligência de IP para Leads B2B",
      "provider": { "@type": "Organization", "name": "AgenteLead Pro" },
      "description": "Enriquecimento de leads com contexto de IP, ASN e detecção de VPN/proxy — conforme LGPD.",
    },
  });
  return (
    <Shell breadcrumb="Identificação por IP"
           title="Identifique empresas prováveis pelo contexto de IP"
           lede="O IP do visitante não é identidade — é contexto. Usamos inteligência de IP para inferir país, estado e cidade aproximados, ASN/organização provável, tipo de conexão (corporativa, residencial, móvel) e flags de VPN/proxy/datacenter. Tudo dentro da LGPD: o IP é truncado e hasheado antes de armazenar.">
      <section>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>O que o sistema extrai</h2>
        <ul>
          <li><strong>Geolocalização aproximada</strong> com raio de precisão e nível de confiança.</li>
          <li><strong>ASN + organização</strong> — identifica a empresa provável quando o IP é corporativo.</li>
          <li><strong>Tipo de conexão:</strong> corporativo, residencial, móvel, datacenter, proxy.</li>
          <li><strong>Flags de segurança:</strong> VPN, Tor, proxy anônimo, hosting.</li>
          <li><strong>Score de confiança</strong> — high quando há cidade + ASN, low quando só país.</li>
        </ul>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Privacidade em primeiro lugar</h2>
        <p>Não armazenamos IP completo por padrão. Cada IP é (a) <strong>truncado</strong> (último octeto IPv4 ou últimos 64 bits IPv6 zerados) e (b) <strong>hasheado</strong> com salt server-side. Cache de 30 dias para reduzir chamadas externas. Apenas dados derivados (cidade, ASN, tipo) ficam na base, jamais o IP original.</p>
      </section>
    </Shell>
  );
}

/* ================================================================== */
/*  5. /crm-para-prospeccao                                           */
/* ================================================================== */
export function CrmParaProspeccao() {
  useSeo({
    title: "CRM para Prospecção B2B — Kanban + Sequenciador + IA — AgenteLead Pro",
    description: "CRM desenhado para outbound: Kanban, cadências multi-canal (email + WhatsApp), Smart Reply Inbox com IA, lookalike, owner lock e funil analítico.",
    canonical: "https://agentelead-pro.com/crm-para-prospeccao",
    jsonLd: {
      "@context": "https://schema.org", "@type": "Service",
      "name": "CRM para Prospecção B2B",
      "provider": { "@type": "Organization", "name": "AgenteLead Pro" },
      "description": "CRM voltado a outbound com sequenciador multi-canal e Smart Reply.",
    },
  });
  return (
    <Shell breadcrumb="CRM para Prospecção"
           title="CRM desenhado para outbound — não adaptado"
           lede="A maioria dos CRMs do mercado foram desenhados para SaaS enterprise (HubSpot, Salesforce) ou solo founders (Pipedrive). O AgenteLead Pro foi desenhado especificamente para equipes que rodam outbound B2B — agências, SDRs locais, consultores. Cada feature existe para gerar conversa real.">
      <section>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>O stack todo em uma plataforma</h2>
        <ul>
          <li><strong>Busca:</strong> Google Maps + LinkedIn Sales Navigator + Instagram + YouTube + SAM.gov.</li>
          <li><strong>Qualificação:</strong> auditoria GPT-4o + Lead Score 0-100 + IP intelligence.</li>
          <li><strong>Outreach:</strong> sequenciador multi-canal (email + WhatsApp) com A/B testing.</li>
          <li><strong>Reply:</strong> Smart Reply Inbox classifica e propõe resposta com IA.</li>
          <li><strong>CRM:</strong> Kanban + Funil + Analytics em uma tela.</li>
        </ul>
      </section>
    </Shell>
  );
}

/* ================================================================== */
/*  6. /politica-de-privacidade                                       */
/* ================================================================== */
export function PoliticaDePrivacidade() {
  useSeo({
    title: "Política de Privacidade — AgenteLead Pro",
    description: "Como o AgenteLead Pro coleta, usa, armazena e compartilha dados de visitantes, leads e usuários. Conforme LGPD (Lei 13.709/2018) e GDPR.",
    canonical: "https://agentelead-pro.com/politica-de-privacidade",
    jsonLd: {
      "@context": "https://schema.org", "@type": "WebPage",
      "name": "Política de Privacidade — AgenteLead Pro",
      "url": "https://agentelead-pro.com/politica-de-privacidade",
      "inLanguage": "pt-BR",
      "datePublished": "2026-05-11",
      "publisher": { "@type": "Organization", "name": "AgenteLead Pro" },
      "about": { "@type": "Thing", "name": "Privacy policy & data protection" },
    },
  });
  return (
    <Shell breadcrumb="Política de Privacidade"
           title="Política de Privacidade"
           lede="Esta política explica como o AgenteLead Pro coleta, usa e armazena seus dados. Atualizada em 11 de maio de 2026. Em conformidade com a LGPD (Lei 13.709/2018), GDPR (UE) e CCPA (Califórnia).">
      <section>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>1. Dados coletados</h2>
        <p>Coletamos as seguintes categorias de dados:</p>
        <ul>
          <li><strong>Cadastro:</strong> nome, email, telefone, empresa, idioma preferido.</li>
          <li><strong>Pagamento:</strong> dados de cartão processados pelo Stripe (não armazenamos PAN — apenas referências tokenizadas).</li>
          <li><strong>Uso da plataforma:</strong> ações, eventos, IPs (truncados), user-agent.</li>
          <li><strong>Leads gerados:</strong> dados de empresas públicas obtidos via Google Maps API e fontes oficiais.</li>
          <li><strong>Conteúdo gerado:</strong> emails, cadências, anotações e auditorias produzidas dentro da plataforma.</li>
        </ul>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>2. Finalidades</h2>
        <ul>
          <li>Prestação do serviço contratado (CRM, busca, outreach).</li>
          <li>Segurança e prevenção a fraude (logs de acesso, anti-abuse).</li>
          <li>Comunicação transacional (confirmações, faturas, alertas).</li>
          <li>Análise agregada para melhorar a plataforma (analytics anonimizado).</li>
          <li>Cumprimento de obrigações legais (fiscais, jurídicas).</li>
        </ul>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>3. Tratamento de IPs</h2>
        <p>IPs de visitantes são <strong>truncados antes do armazenamento</strong> (último octeto IPv4 zerado, últimos 64 bits IPv6 zerados) e <strong>hasheados com salt server-side</strong>. Nunca armazenamos o IP completo de visitantes não autenticados.</p>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>4. Compartilhamento</h2>
        <p>Compartilhamos dados apenas com processadores estritamente necessários: Stripe (pagamentos), AWS/MongoDB Atlas (armazenamento), ipinfo.io (enriquecimento de IP), Resend (entrega de email), Slack (alertas internos do master), Google (Maps API), Apify (scrapers de redes sociais), OpenAI (geração de auditorias GPT-4o). Nenhum dado pessoal é vendido a terceiros.</p>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>5. Seus direitos (LGPD/GDPR)</h2>
        <p>Você pode solicitar a qualquer momento: confirmação de tratamento, acesso aos dados, correção, anonimização, eliminação, portabilidade e informações sobre compartilhamento. Para exercer, envie email para <strong>privacidade@agentelead-pro.com</strong>. Atendemos em até 15 dias.</p>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>6. Cookies</h2>
        <p>Usamos cookies essenciais (sessão, segurança) e cookies de analytics anonimizado (Posthog). Cookies de marketing/remarketing apenas com consentimento explícito via banner.</p>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>7. Retenção</h2>
        <p>Dados de cadastro: enquanto a conta existir + 5 anos para fins fiscais. Logs de acesso: 6 meses. IPs hasheados: 30 dias (cache de enriquecimento). Solicite remoção a qualquer momento.</p>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>8. Encarregado (DPO)</h2>
        <p>Email: <strong>dpo@agentelead-pro.com</strong>. Tempo de resposta: 5 dias úteis para casos simples, 15 dias para complexos.</p>
      </section>
    </Shell>
  );
}

/* ================================================================== */
/*  7. /lgpd                                                          */
/* ================================================================== */
export function LGPD() {
  useSeo({
    title: "LGPD — Conformidade do AgenteLead Pro com a Lei 13.709/2018",
    description: "Como o AgenteLead Pro implementa cada um dos 10 princípios da LGPD: finalidade, adequação, necessidade, livre acesso, qualidade, transparência, segurança, prevenção, não discriminação e responsabilização.",
    canonical: "https://agentelead-pro.com/lgpd",
    jsonLd: {
      "@context": "https://schema.org", "@type": "WebPage",
      "name": "Conformidade LGPD — AgenteLead Pro",
      "url": "https://agentelead-pro.com/lgpd",
      "inLanguage": "pt-BR",
      "datePublished": "2026-05-11",
      "publisher": { "@type": "Organization", "name": "AgenteLead Pro" },
      "about": { "@type": "Thing", "name": "LGPD compliance (Lei 13.709/2018)" },
    },
  });
  return (
    <Shell breadcrumb="LGPD"
           title="Conformidade LGPD"
           lede="O AgenteLead Pro foi projetado dentro dos 10 princípios da Lei 13.709/2018 (LGPD). Esta página detalha como aplicamos cada princípio na prática técnica e operacional.">
      <section>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Os 10 princípios — implementação</h2>
        <dl>
          {[
            ["Finalidade", "Cada coleta tem propósito declarado (prestação do serviço, segurança, transação, analytics anonimizado)."],
            ["Adequação", "Dados coletados são compatíveis com o serviço contratado — não usamos dados de leads para fins não declarados."],
            ["Necessidade", "Minimização: IP truncado, sem PAN de cartão, sem dados sensíveis. Hash de IP usa salt server-side."],
            ["Livre acesso", "Toda conta pode exportar seus dados via Settings → Privacy → Export, formato JSON."],
            ["Qualidade dos dados", "Validação de email/telefone no cadastro; correções via Account → Profile a qualquer momento."],
            ["Transparência", "Esta política + a Política de Privacidade são públicas e indexáveis. Mudanças notificadas por email com 30 dias de antecedência."],
            ["Segurança", "Senhas hasheadas com bcrypt (12 rounds), TLS 1.3, backup criptografado, RBAC, audit log imutável."],
            ["Prevenção", "Rate limiting, brute-force lockout, detecção de anomalias por IP, alerts no Slack do master."],
            ["Não discriminação", "Lead score nunca usa dados sensíveis (raça, religião, orientação) — apenas dados de negócio públicos."],
            ["Responsabilização", "Audit log completo de qualquer acesso administrativo. DPO disponível em dpo@agentelead-pro.com."],
          ].map(([k, v]) => (
            <div key={k}>
              <dt style={{ fontWeight: 700, marginTop: 14 }}>{k}</dt>
              <dd style={{ margin: "4px 0 0", color: "#334155" }}>{v}</dd>
            </div>
          ))}
        </dl>
      </section>
    </Shell>
  );
}

/* ================================================================== */
/*  8. /seguranca                                                     */
/* ================================================================== */
export function Seguranca() {
  useSeo({
    title: "Segurança — AgenteLead Pro",
    description: "Práticas de segurança: criptografia em trânsito (TLS 1.3) e em repouso, autenticação multi-fator (TOTP), audit logs imutáveis, RBAC, isolamento multi-tenant.",
    canonical: "https://agentelead-pro.com/seguranca",
    jsonLd: {
      "@context": "https://schema.org", "@type": "WebPage",
      "name": "Segurança — AgenteLead Pro",
      "url": "https://agentelead-pro.com/seguranca",
      "inLanguage": "pt-BR",
      "publisher": { "@type": "Organization", "name": "AgenteLead Pro" },
      "about": { "@type": "Thing", "name": "Application & infrastructure security" },
    },
  });
  return (
    <Shell breadcrumb="Segurança"
           title="Segurança em camadas"
           lede="Construímos o AgenteLead Pro com defense-in-depth. Cada camada — rede, aplicação, dados, operacional — tem controles dedicados e auditáveis.">
      <section>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Criptografia</h2>
        <ul>
          <li><strong>Em trânsito:</strong> TLS 1.3 obrigatório, HSTS em todos os domínios, certificados gerenciados.</li>
          <li><strong>Em repouso:</strong> MongoDB Atlas com encryption-at-rest (AES-256).</li>
          <li><strong>Senhas:</strong> bcrypt com 12 rounds + salt aleatório por usuário.</li>
          <li><strong>Tokens:</strong> JWTs assinados HS256, TTL 24h, rotacionáveis.</li>
        </ul>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Autenticação</h2>
        <ul>
          <li>Login via email/senha ou Google OAuth (Emergent-managed).</li>
          <li>2FA opcional via TOTP (Google Authenticator, 1Password, Authy).</li>
          <li>Rate limiting + brute-force lockout (5 falhas → 15 min).</li>
          <li>Magic links com TTL configurável (default 14 dias) para acessos delegados.</li>
        </ul>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Isolamento multi-tenant</h2>
        <ul>
          <li>Cada tenant tem ID único; toda query é filtrada por tenant_id no nível do banco.</li>
          <li>Master Console possui audit log completo; impersonação fica registrada com timestamp.</li>
          <li>RBAC com roles: super_admin, admin, master, cliente_admin, agency_member.</li>
        </ul>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Audit Log</h2>
        <p>Toda ação administrativa relevante é registrada com user_id, action, target, timestamp e diff. Log é append-only e exportável para SIEM. Retenção: 12 meses.</p>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "32px 0 8px" }}>Disclosure responsável</h2>
        <p>Reporte vulnerabilidades para <strong>security@agentelead-pro.com</strong>. SLA: ack em 48h, fix crítico em 7 dias. Não temos bug bounty formal mas reconhecemos publicamente pesquisadores éticos.</p>
      </section>
    </Shell>
  );
}
